import React, { useEffect, useState } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { Provider, useDispatch, useSelector } from 'react-redux';
import { store } from './redux/store';
import Header from './components/Header';
import MobileGlobalHeader from './components/mobile/MobileGlobalHeader';
import { AuthProvider } from './context/AuthContext';
import { GameProvider } from './context/GameContext';
import { SeasonProvider } from './context/SeasonContext';
import UnauthorizedHandler from './components/UnauthorizedHandler';
import { CurrentWeekProvider } from './context/CurrentWeekContext';
import { MobileProvider, useMobile } from './context/MobileContext';
import { FilterProvider } from './context/FilterContext';
import { SortProvider } from './context/SortContext';
import { LeagueSettingsProvider } from './context/LeagueSettingsContext';
import AppRoutes from './routes';
import { initializeAuth } from './redux/authSlice';
import PlayoffNotificationModal from './components/PlayoffNotificationModal';
import api from './api';


import 'bootstrap/dist/css/bootstrap.min.css';

// Conditional Header Component
const ConditionalHeader = () => {
  const { isMobile } = useMobile();
  return isMobile ? <MobileGlobalHeader /> : <Header />;
};

// Create a wrapper component to handle auth initialization
const AppContent = () => {
  const dispatch = useDispatch();
  const [isInitializing, setIsInitializing] = useState(true);
  const authStatus = useSelector(state => state.auth.status);
  const [showPlayoffModal, setShowPlayoffModal] = useState(false);
  const [modalDismissed, setModalDismissed] = useState(false);

  useEffect(() => {
    const initialize = async () => {
      dispatch(initializeAuth());
      setIsInitializing(false);
    };
    initialize();
  }, [dispatch]);

  // Check for playoff prediction requirement after login
  useEffect(() => {
    const checkPlayoffModal = async () => {
      if (authStatus === 'succeeded' && !modalDismissed) {
        try {
          // Small delay to ensure user context is loaded
          setTimeout(async () => {
            try {
              const response = await api.getPlayoffStatus();
              if (response.data.exists && response.data.playoff.needs_prediction) {
                setShowPlayoffModal(true);
              }
            } catch (err) {
              // Silently fail - modal is optional
            }
          }, 1000);
        } catch (err) {
          // Silently fail - modal is optional
        }
      }
    };
    checkPlayoffModal();
  }, [authStatus, modalDismissed]);

  if (isInitializing || authStatus === 'loading') {
    return <div>Loading...</div>; // Or your loading component
  }

  return (
    <MobileProvider>
      <AuthProvider>
        <SeasonProvider>
          <CurrentWeekProvider>
            <LeagueSettingsProvider>
              <GameProvider>
                <FilterProvider>
                  <SortProvider>
                    <UnauthorizedHandler />
                    <ConditionalHeader />
                    <AppRoutes />
                    <PlayoffNotificationModal
                      isOpen={showPlayoffModal}
                      onClose={() => setShowPlayoffModal(false)}
                      onDismiss={() => {
                        setShowPlayoffModal(false);
                        setModalDismissed(true);
                      }}
                    />
                  </SortProvider>
                </FilterProvider>
              </GameProvider>
            </LeagueSettingsProvider>
          </CurrentWeekProvider>
        </SeasonProvider>
      </AuthProvider>
    </MobileProvider>
  );
};

function App() {
  return (
    <Provider store={store}>
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </Provider>
  );
}

export default App;
